/**
 * Aether Affiliate Detail Page.
 *
 * Opened from the Affiliates tab when an admin clicks an affiliate row.
 * Renders a per-affiliate breakdown: header w/ tracking code + linked
 * login, 4 stat cards (sales / revenue / commission / unique customers),
 * and three tabs: Übersicht (per-product roll-up), Bestellungen (the
 * paid orders carrying this code), Profil (raw affiliate + linked user).
 *
 * Data comes from a single GET /api/affiliates/{id}/detail call.
 */
import React, { useEffect, useState } from 'react';
import {
  ArrowLeft, ChevronRight, Loader2, Package, DollarSign, TrendingUp,
  Users as UsersIcon, Link as LinkIcon, Check, Mail, ExternalLink, Hash,
  ShoppingCart, AlertCircle,
} from 'lucide-react';
import { useAetherAuth } from './AetherAuthContext';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const fmtMoney = (n, ccy = 'eur') =>
  `${(Number(n) || 0).toFixed(2)} ${(ccy || 'eur').toUpperCase() === 'EUR' ? '€' : ccy.toUpperCase()}`;

export default function AffiliateDetail({ affiliateId, onBack, onOpenUser }) {
  const { authFetch } = useAetherAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('overview');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr('');
    authFetch(`/api/affiliates/${affiliateId}/detail`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) throw new Error(d.detail || 'Fehler beim Laden');
        setData(d);
      })
      .catch(e => !cancelled && setErr(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [affiliateId, authFetch]);

  const copyLink = (code) => {
    const url = `${window.location.origin}?ref=${code}`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  if (loading) {
    return (
      <div className="w-full">
        <button onClick={onBack} className="mb-6 flex items-center gap-2 text-xs text-white/40 hover:text-white">
          <ArrowLeft size={14} /> Zurück
        </button>
        <div className="flex items-center justify-center py-24 text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="w-full">
        <button onClick={onBack} className="mb-6 flex items-center gap-2 text-xs text-white/40 hover:text-white">
          <ArrowLeft size={14} /> Zurück
        </button>
        <div className="p-4 rounded-lg bg-red-500/10 text-red-300 flex items-center gap-2 text-sm">
          <AlertCircle size={16} /> {err || 'Affiliate nicht gefunden'}
        </div>
      </div>
    );
  }

  const { affiliate, linked_user, orders, stats, by_product } = data;

  return (
    <div className="w-full" data-testid="aether-affiliate-detail">
      {/* Breadcrumb + back */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-xs">
          <button onClick={onBack} className="text-white/40 hover:text-white">Affiliates</button>
          <ChevronRight size={12} className="text-white/30" />
          <span className="text-white/70">{affiliate.name || affiliate.code}</span>
        </div>
        <button onClick={onBack} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-white/60 hover:text-white liquid-glass">
          <ArrowLeft size={13} /> Zurück
        </button>
      </div>

      {/* Header card */}
      <div className="monolith-glass p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-4 rounded-xl bg-white/5 shrink-0">
            <LinkIcon size={28} className="text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-1">Affiliate</p>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold text-white truncate">{affiliate.name || affiliate.email}</h1>
              <span className="px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest bg-orange-400/15 text-orange-300">
                {affiliate.is_active === false ? 'Inaktiv' : 'Aktiv'}
              </span>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-mono text-white/50 bg-white/5">
                {Math.round((Number(affiliate.commission_rate) || 0) * 100)}% Provision
              </span>
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] font-mono">
              <div className="flex items-center"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Code</span>
                <code className="text-orange-300 bg-orange-300/10 px-2 py-0.5 rounded">{affiliate.code}</code>
                <button
                  onClick={() => copyLink(affiliate.code)}
                  className="ml-2 text-white/30 hover:text-white"
                  title="Affiliate-Link kopieren"
                >
                  {copied ? <Check size={12} className="text-emerald-400" /> : <LinkIcon size={12} />}
                </button>
              </div>
              <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">E-Mail</span>
                <span className="text-white/80 truncate">{affiliate.email || '—'}</span>
              </div>
              <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Angelegt</span>
                <span className="text-white/80">{fmtDate(affiliate.created_at)}</span>
              </div>
              <div className="flex items-center"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Login</span>
                {linked_user ? (
                  <button
                    onClick={() => onOpenUser && onOpenUser(linked_user)}
                    className="text-orange-300 hover:text-white truncate flex items-center gap-1"
                    title="Verknüpften Portal-Nutzer öffnen"
                  >
                    {linked_user.name || linked_user.email}
                    <ExternalLink size={10} />
                  </button>
                ) : (
                  <span className="text-amber-400/70">Keine Verknüpfung</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Verkäufe"        value={stats.total_sales}                          icon={ShoppingCart} accent="text-sky-400" />
        <Stat label="Umsatz"          value={fmtMoney(stats.total_revenue)}              icon={DollarSign}   accent="text-emerald-400" />
        <Stat label="Provisionen"     value={fmtMoney(stats.total_commission)}           icon={TrendingUp}   accent="text-orange-400" />
        <Stat label="Unique Kunden"   value={stats.unique_customers}                     icon={UsersIcon}    accent="text-violet-400" />
      </div>

      {/* Tabs */}
      <div className="border-b mb-4 flex gap-1" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        {[
          { id: 'overview', label: 'Übersicht' },
          { id: 'orders',   label: `Bestellungen (${orders.length})` },
          { id: 'profile',  label: 'Profil' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition ${
              tab === t.id ? 'text-white border-white' : 'text-white/40 border-transparent hover:text-white/70'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <OverviewTab byProduct={by_product} orders={orders} affiliate={affiliate} />
      )}
      {tab === 'orders' && (
        <OrdersTab orders={orders} />
      )}
      {tab === 'profile' && (
        <ProfileTab affiliate={affiliate} linkedUser={linked_user} onOpenUser={onOpenUser} />
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent = 'text-white' }) {
  return (
    <div className="monolith-glass p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className={accent} />
        <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</span>
      </div>
      <p className="text-xl font-semibold text-white">{value}</p>
    </div>
  );
}

function OverviewTab({ byProduct, orders, affiliate }) {
  // Sort products by revenue descending — most valuable first.
  const sorted = [...(byProduct || [])].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
  const last5 = orders.slice(0, 5);
  return (
    <div className="space-y-6">
      {/* Per-product breakdown */}
      <section>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Pro Produkt</h3>
        {sorted.length === 0 ? (
          <div className="monolith-glass p-6 text-center text-white/30 text-xs font-mono uppercase tracking-widest">
            Noch keine Verkäufe
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map(p => (
              <div key={p.product_name} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{p.product_name}</p>
                  <p className="font-mono text-[10px] text-white/40">{p.sales} Verkauf{p.sales !== 1 ? 'e' : ''}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-white">{fmtMoney(p.revenue)}</p>
                  <p className="font-mono text-[10px] text-orange-300">{fmtMoney(p.commission)} Provision</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent orders preview */}
      <section>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Letzte Bestellungen</h3>
        {last5.length === 0 ? (
          <div className="monolith-glass p-6 text-center text-white/30 text-xs font-mono uppercase tracking-widest">
            Noch keine Bestellungen
          </div>
        ) : (
          <div className="space-y-2">
            {last5.map(o => <OrderRow key={o.id || o.order_number} o={o} compact />)}
          </div>
        )}
      </section>

      {/* Quick share */}
      <section>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Affiliate-Link</h3>
        <div className="monolith-glass p-4 flex items-center gap-3">
          <Hash size={14} className="text-orange-400 shrink-0" />
          <code className="font-mono text-[11px] text-white/70 flex-1 truncate">
            {window.location.origin}?ref={affiliate.code}
          </code>
        </div>
      </section>
    </div>
  );
}

function OrdersTab({ orders }) {
  if (orders.length === 0) {
    return (
      <div className="monolith-glass p-10 text-center text-white/30 text-xs font-mono uppercase tracking-widest">
        Über diesen Code wurden noch keine Bestellungen abgeschlossen.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {orders.map(o => <OrderRow key={o.id || o.order_number} o={o} />)}
    </div>
  );
}

function OrderRow({ o, compact = false }) {
  return (
    <div className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-white truncate">{o.product_name || '—'}</span>
          {o.order_number && (
            <code className="font-mono text-[9px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">#{o.order_number}</code>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-white/40 flex-wrap">
          {o.customer_email && (
            <span className="flex items-center gap-1"><Mail size={10} />{o.customer_email}</span>
          )}
          <span>{fmtDate(o.created_at)}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="font-mono text-sm text-white">{fmtMoney(o.amount, o.currency)}</p>
        {!compact && (
          <p className="font-mono text-[10px] text-orange-300">{fmtMoney(o.commission, o.currency)} Provision</p>
        )}
      </div>
    </div>
  );
}

function ProfileTab({ affiliate, linkedUser, onOpenUser }) {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Affiliate-Datensatz</h3>
        <div className="monolith-glass p-5 space-y-2 font-mono text-[11px]">
          <Row label="ID"          value={<code className="text-white/60">{affiliate.id}</code>} />
          <Row label="Code"        value={<code className="text-orange-300 bg-orange-300/10 px-2 py-0.5 rounded">{affiliate.code}</code>} />
          <Row label="Name"        value={affiliate.name || '—'} />
          <Row label="E-Mail"      value={affiliate.email || '—'} />
          <Row label="Provision"   value={`${Math.round((Number(affiliate.commission_rate) || 0) * 100)}%`} />
          <Row label="Aktiv"       value={affiliate.is_active === false ? 'nein' : 'ja'} />
          <Row label="Angelegt"    value={fmtDate(affiliate.created_at)} />
          <Row label="Total Sales (gesamt)"       value={affiliate.total_sales ?? 0} />
          <Row label="Total Revenue (gesamt)"     value={fmtMoney(affiliate.total_revenue)} />
          <Row label="Total Commission (gesamt)"  value={fmtMoney(affiliate.total_commission)} />
        </div>
      </section>

      <section>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Verknüpfter Aether-Login</h3>
        {linkedUser ? (
          <div className="monolith-glass p-5 space-y-2 font-mono text-[11px]">
            <Row label="Name"   value={linkedUser.name || '—'} />
            <Row label="E-Mail" value={linkedUser.email} />
            <Row label="Rolle"  value={linkedUser.role} />
            <Row label="Aktiv"  value={linkedUser.is_active ? 'ja' : 'nein'} />
            <Row label="Firma"  value={linkedUser.company || '—'} />
            <Row label="Letzter Login" value={linkedUser.last_login ? fmtDate(linkedUser.last_login) : 'nie'} />
            {onOpenUser && (
              <button
                onClick={() => onOpenUser(linkedUser)}
                className="mt-3 px-3 py-1.5 rounded-lg text-xs flex items-center gap-2 liquid-glass hover:bg-white/10 text-white/60 hover:text-white"
              >
                <ExternalLink size={12} /> In der Nutzerverwaltung öffnen
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <AlertCircle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed text-white/70">
              Dieser Affiliate ist mit keinem Aether-Nutzer verknüpft. Solche Einträge entstanden bei alten Datensätzen vor der Konsolidierung. Du kannst ihn entweder löschen oder einen Partner-User mit derselben E-Mail anlegen und ihn dort über das Profil verknüpfen.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-white/30 uppercase tracking-widest text-[9px] w-44 shrink-0 pt-0.5">{label}</span>
      <span className="text-white/80 flex-1 break-all">{value}</span>
    </div>
  );
}
